import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
    Users,
    Inbox,
    LayoutGrid,
    Database,
    Wallet,
    DollarSign,
    TrendingUp,
    Activity,
    Send,
    Sparkles,
    Clock,
    UserPlus,
    FileText,
} from "lucide-react";

function StatCard({ Icon, label, value, sublabel, link, accent, testId }) {
    const Wrapper = link ? Link : "div";
    const wrapperProps = link ? { to: link } : {};
    return (
        <Wrapper
            {...wrapperProps}
            data-testid={testId}
            className={`group relative bg-card border border-white/[0.07] rounded-2xl p-5 ${
                link ? "hover:border-white/20 transition-colors cursor-pointer" : ""
            }`}
        >
            <div className="flex items-start justify-between mb-3">
                <div
                    className="w-10 h-10 rounded-xl grid place-items-center"
                    style={{
                        background: accent ? `${accent}15` : "rgba(255,255,255,0.04)",
                        color: accent || "rgb(229, 231, 235)",
                        border: `1px solid ${accent ? accent + "30" : "rgba(255,255,255,0.08)"}`,
                    }}
                >
                    <Icon className="w-5 h-5" />
                </div>
                {link && (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                        Open →
                    </span>
                )}
            </div>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
                {label}
            </p>
            <p className="font-display font-black text-2xl md:text-3xl mt-1 tabular-nums">
                {value}
            </p>
            {sublabel && (
                <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                    {sublabel}
                </p>
            )}
        </Wrapper>
    );
}

const ACTIVITY_ICON = {
    user_signup: { Icon: UserPlus, accent: "#9333EA" },
    application: { Icon: FileText, accent: "#22D3EE" },
    payment: { Icon: DollarSign, accent: "#10B981" },
    ad_account: { Icon: Database, accent: "#2FA57C" },
};

function relativeTime(iso) {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

function ActivityFeed({ activity }) {
    return (
        <div className="bg-card border border-white/[0.07] rounded-2xl">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                <div>
                    <h3 className="font-display font-semibold">Live activity</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        Last 50 events across collections
                    </p>
                </div>
                <Activity className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="divide-y divide-white/[0.04] max-h-[480px] overflow-y-auto">
                {activity.length === 0 && (
                    <div className="px-5 py-12 text-center text-sm text-muted-foreground">
                        No activity yet.
                    </div>
                )}
                {activity.map((a, i) => {
                    const cfg = ACTIVITY_ICON[a.type] || {
                        Icon: Activity,
                        accent: "#94A3B8",
                    };
                    return (
                        <div
                            key={i}
                            data-testid={`activity-${a.type}-${i}`}
                            className="px-5 py-3 flex items-start gap-3 hover:bg-white/[0.02] transition-colors"
                        >
                            <div
                                className="w-8 h-8 rounded-lg grid place-items-center shrink-0"
                                style={{
                                    background: `${cfg.accent}15`,
                                    color: cfg.accent,
                                    border: `1px solid ${cfg.accent}30`,
                                }}
                            >
                                <cfg.Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">{a.title}</p>
                                <p className="text-[11px] text-muted-foreground truncate">
                                    {a.subtitle}
                                </p>
                            </div>
                            <span className="text-[10px] font-mono text-muted-foreground shrink-0 mt-1">
                                <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                                {relativeTime(a.ts)}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [activity, setActivity] = useState([]);
    const [deposits, setDeposits] = useState(null);

    const load = async () => {
        const [s, a, d] = await Promise.allSettled([
            api.get("/admin/stats"),
            api.get("/admin/activity"),
            api.get("/admin/deposits", { params: { limit: 1 } }),
        ]);
        if (s.status === "fulfilled") setStats(s.value.data);
        if (a.status === "fulfilled") setActivity(a.value.data);
        if (d.status === "fulfilled") setDeposits(d.value.data);
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, []);

    return (
        <div data-testid="admin-dashboard" className="space-y-8">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                        Overview
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Track users, revenue, applications and operations in one screen.
                    </p>
                </div>
                <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Auto-refresh 30s
                </span>
            </div>

            <div className="grid md:grid-cols-4 gap-3" data-testid="admin-quick-actions">
                <Link to="/admin/deposits" data-testid="quick-action-deposits" className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3 text-sm hover:bg-emerald-500/[0.10] transition-colors">
                    Gelen Ödemeler → Kim, ne kadar, yüklendi mi?
                </Link>
                <Link to="/admin/sweep" data-testid="quick-action-sweep" className="rounded-xl border border-primary/20 bg-primary/[0.05] px-4 py-3 text-sm hover:bg-primary/[0.08] transition-colors">
                    Sweep Center → Move incoming USDT/USDC
                </Link>
                <Link to="/admin/wallets" data-testid="quick-action-wallets" className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm hover:bg-white/[0.05] transition-colors">
                    Wallet Addresses → Manage manual addresses
                </Link>
                <Link to="/admin/site-settings" data-testid="quick-action-cms" className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm hover:bg-white/[0.05] transition-colors">
                    Site Content → Update copy and links
                </Link>
            </div>

            {/* Top row — Revenue + MRR */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    Icon={DollarSign}
                    label="Revenue (today)"
                    value={`$${(stats?.revenue_today_usd || 0).toLocaleString()}`}
                    sublabel={deposits ? `${deposits.summary.today_count} deposit · ${deposits.summary.confirming_count} confirming` : "Completed in last 24h"}
                    link="/admin/deposits"
                    accent="#10B981"
                    testId="stat-revenue-today"
                />
                <StatCard
                    Icon={TrendingUp}
                    label="Revenue (month)"
                    value={`$${(stats?.revenue_month_usd || 0).toLocaleString()}`}
                    sublabel="Month to date"
                    accent="#10B981"
                    testId="stat-revenue-month"
                />
                <StatCard
                    Icon={Sparkles}
                    label="Deposits (month)"
                    value={`$${(deposits?.summary?.month_usd || 0).toLocaleString()}`}
                    sublabel={`${deposits?.summary?.month_count || 0} on-chain deposits credited`}
                    link="/admin/deposits"
                    accent="#2FA57C"
                    testId="stat-deposits-month"
                />
                <StatCard
                    Icon={Wallet}
                    label="Wallets total"
                    value={`$${(stats?.total_wallet_usd || 0).toLocaleString()}`}
                    sublabel="USD held in user wallets"
                    accent="#22D3EE"
                    testId="stat-wallet-total"
                />
            </div>

            {/* Operations row */}
            <div>
                <h3 className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-3">
                    Operations
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        Icon={Inbox}
                        label="Pending applications"
                        value={stats?.pending_applications ?? "—"}
                        sublabel="Awaiting review"
                        link="/admin/applications"
                        accent="#2FA57C"
                        testId="stat-pending-apps"
                    />
                    <StatCard
                        Icon={Database}
                        label="Pending ad accounts"
                        value={stats?.pending_ad_accounts ?? "—"}
                        sublabel="To provision"
                        link="/admin/ad-accounts"
                        accent="#2FA57C"
                        testId="stat-pending-accounts"
                    />
                    <StatCard
                        Icon={Send}
                        label="Pending orders"
                        value={stats?.pending_orders ?? "—"}
                        sublabel="On-chain confirming"
                        accent="#FBBF24"
                        testId="stat-pending-orders"
                    />
                    <StatCard
                        Icon={Activity}
                        label="Transactions (24h)"
                        value={stats?.transactions_24h ?? "—"}
                        link="/admin/transactions"
                        accent="#94A3B8"
                        testId="stat-txns-24h"
                    />
                </div>
            </div>

            {/* Audience row */}
            <div>
                <h3 className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-3">
                    Audience
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        Icon={Users}
                        label="Total users"
                        value={stats?.total_users ?? "—"}
                        link="/admin/users"
                        accent="#9333EA"
                        testId="stat-total-users"
                    />
                    <StatCard
                        Icon={UserPlus}
                        label="Active users"
                        value={stats?.active_users ?? "—"}
                        sublabel={`${stats?.conversion_rate ?? 0}% conversion`}
                        accent="#9333EA"
                        testId="stat-active-users"
                    />
                    <StatCard
                        Icon={LayoutGrid}
                        label="Confirming deposits"
                        value={deposits?.summary?.confirming_count ?? "—"}
                        link="/admin/deposits"
                        accent="#2FA57C"
                        testId="stat-confirming-deposits"
                    />
                    <StatCard
                        Icon={Database}
                        label="Active ad accounts"
                        value={stats?.active_ad_accounts ?? "—"}
                        link="/admin/ad-accounts"
                        accent="#10B981"
                        testId="stat-active-accounts"
                    />
                </div>
            </div>

            {/* Activity feed */}
            <ActivityFeed activity={activity} />
        </div>
    );
}
